import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import FloatingNameTagWithEvents from './FloatingNameTagWithEvents';

/**
 * A friendly procedural sheet-ghost: the default look for people without an
 * avatar (view-only visitors) and for a peer whose avatar is still loading.
 *
 * Built once from a LatheGeometry profile (dome head, flaring body, scalloped
 * hem) plus a few tiny spheres for eyes/mouth/arm nubs; all geometry and
 * materials are module-level singletons shared by every ghost, so a room full
 * of them costs ~2k triangles and a couple of draw calls each. The hem ripple
 * runs in the vertex shader off one shared time uniform; bob/sway are per
 * ghost (phase from the name) in a single useFrame.
 */

export interface GhostTransform {
  position: [number, number, number];
  rotation: number;
}

// ---- Dimensions (metres) ----
const HOVER = 0.18;       // gap between floor and the hem's lowest point
const BODY_H = 1.45;      // hem bottom -> top of the dome
const HEAD_R = 0.3;       // dome radius
const HEM_R = 0.5;        // radius at the hem (the flare)
const SCALLOPS = 6;       // hem waves around the circumference
const SCALLOP_DEPTH = 0.1;
const HEM_BAND = 0.35;    // how far up the body the hem waves/ripple fade out
const RADIAL_SEGS = 36;

// ---- Shared resources (built lazily, never disposed: they are tiny) ----
let shared: {
  body: THREE.BufferGeometry;
  eye: THREE.BufferGeometry;
  mouth: THREE.BufferGeometry;
  arm: THREE.BufferGeometry;
  shadow: THREE.BufferGeometry;
  bodyMat: THREE.MeshStandardMaterial;
  bodyMatLoading: THREE.MeshStandardMaterial;
  faceMat: THREE.MeshBasicMaterial;
  shadowMat: THREE.MeshBasicMaterial;
  time: { value: number };
} | null = null;

const _hemTint = new THREE.Color('#9fe9df');
const _crownTint = new THREE.Color('#ffffff');
const _tint = new THREE.Color();
function tintAt(t: number, out: Float32Array, o: number) {
  _tint.copy(_hemTint).lerp(_crownTint, Math.pow(t, 0.7));
  out[o] = _tint.r; out[o + 1] = _tint.g; out[o + 2] = _tint.b;
}

function uniformTint(geo: THREE.BufferGeometry, t: number): THREE.BufferGeometry {
  const n = geo.attributes.position.count;
  const col = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) tintAt(t, col, i * 3);
  geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
  return geo;
}

function buildBodyGeometry(): THREE.BufferGeometry {
  // Profile from the underside centre, out to the hem, up the body, over the dome.
  const pts: THREE.Vector2[] = [];
  // Concave underside so the mesh is closed (no see-through inside).
  pts.push(new THREE.Vector2(0.001, 0.22));
  pts.push(new THREE.Vector2(HEM_R * 0.55, 0.13));
  pts.push(new THREE.Vector2(HEM_R * 0.9, 0.03));
  pts.push(new THREE.Vector2(HEM_R, 0));
  // Body: flares from the head radius (at the dome's equator) down to the hem.
  const shoulderY = BODY_H - HEAD_R;
  const bodySteps = 9;
  for (let i = 1; i <= bodySteps; i++) {
    const t = i / bodySteps;                 // 0 at hem -> 1 at shoulder
    const y = t * shoulderY;
    const flare = Math.pow(1 - t, 2.2);      // flare concentrated near the hem
    const r = HEAD_R + 0.015 + (HEM_R - HEAD_R - 0.015) * flare;
    pts.push(new THREE.Vector2(r, y));
  }
  // Dome
  const domeSteps = 8;
  for (let i = 1; i <= domeSteps; i++) {
    const a = (i / domeSteps) * (Math.PI / 2);
    pts.push(new THREE.Vector2(Math.max(0.001, (HEAD_R + 0.015) * Math.cos(a)), shoulderY + HEAD_R * Math.sin(a)));
  }

  const geo = new THREE.LatheGeometry(pts, RADIAL_SEGS);
  // Scalloped hem + a per-vertex weight the shader uses for the ripple.
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const hemW = new Float32Array(pos.count);
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
    const r = Math.hypot(x, z);
    // Weight: 1 at the hem, fading out up the body; underside weighted by radius.
    let w = y < HEM_BAND ? 1 - y / HEM_BAND : 0;
    if (y > 0.001 && r < HEM_R * 0.95 && y <= 0.22) w = Math.min(w, r / HEM_R); // underside
    w = Math.max(0, w);
    w = w * w * (3 - 2 * w);
    hemW[i] = w;
    const theta = Math.atan2(z, x);
    // Rounded lobes that hang down (classic sheet-ghost hem).
    const lobe = 0.5 + 0.5 * Math.cos(SCALLOPS * theta);
    pos.setY(i, y - SCALLOP_DEPTH * lobe * w);
  }
  geo.setAttribute('hemW', new THREE.BufferAttribute(hemW, 1));
  // Vertical tint: pale teal at the hem up to near-white at the crown.
  const col = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const t = Math.min(1, Math.max(0, pos.getY(i) / BODY_H));
    tintAt(t, col, i * 3);
  }
  geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
  geo.computeVertexNormals();
  geo.translate(0, HOVER + SCALLOP_DEPTH, 0);
  return geo;
}

function makeBodyMaterial(opacity: number, time: { value: number }): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({
    color: '#e8fffb',
    emissive: '#5ee6dc',
    emissiveIntensity: 0.12,   // low: reads as a glow without blowing out under bloom
    roughness: 0.45,
    vertexColors: true,
    metalness: 0,
    transparent: true,
    opacity,
  });
  m.onBeforeCompile = (shader) => {
    shader.uniforms.uGhostTime = time;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute float hemW;\nuniform float uGhostTime;')
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        {
          float th = atan(position.z, position.x);
          float rip = sin(th * ${SCALLOPS.toFixed(1)} - uGhostTime * 2.2) * 0.5 + sin(th * 3.0 + uGhostTime * 1.3) * 0.5;
          transformed.y += rip * 0.025 * hemW;
          transformed.xz *= 1.0 + rip * 0.035 * hemW;
        }`,
      );
    // Fresnel rim: edges glow a little and turn more opaque, which gives the
    // translucent sheet a readable 3D silhouette in dim rooms.
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <emissivemap_fragment>',
      `#include <emissivemap_fragment>
      {
        float ghostRim = pow(1.0 - saturate(abs(dot(normal, normalize(vViewPosition)))), 2.2);
        totalEmissiveRadiance += vec3(0.55, 0.95, 0.9) * ghostRim * 0.35;
        diffuseColor.a = mix(diffuseColor.a, 1.0, ghostRim * 0.6);
      }`,
    );
  };
  m.customProgramCacheKey = () => 'gathrxr-ghost-body';
  return m;
}

function makeShadowTexture(): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  grad.addColorStop(0, 'rgba(0,0,0,0.55)');
  grad.addColorStop(0.6, 'rgba(0,0,0,0.2)');
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 64, 64);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

function getShared() {
  if (shared) return shared;
  const time = { value: 0 };
  const shadow = new THREE.CircleGeometry(0.5, 20);
  shadow.rotateX(-Math.PI / 2);
  shared = {
    body: buildBodyGeometry(),
    eye: new THREE.SphereGeometry(1, 8, 6),
    mouth: new THREE.SphereGeometry(1, 8, 6),
    arm: uniformTint(new THREE.SphereGeometry(1, 8, 6), 0.5),
    shadow,
    bodyMat: makeBodyMaterial(0.82, time),
    bodyMatLoading: makeBodyMaterial(0.5, time),
    faceMat: new THREE.MeshBasicMaterial({ color: '#16202a' }),
    shadowMat: new THREE.MeshBasicMaterial({ map: makeShadowTexture(), transparent: true, depthWrite: false, opacity: 0.6 }),
    time,
  };
  return shared;
}

function hashPhase(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return ((h >>> 0) % 10000) / 10000 * Math.PI * 2;
}

const _target = new THREE.Vector3();

// Face sits on the dome's front (+Z, the direction avatars face at rotation 0).
const EYE_Y = HOVER + SCALLOP_DEPTH + BODY_H - HEAD_R + 0.03;
const SURF_R = HEAD_R + 0.015;
const EYE_X = 0.1;
const EYE_Z = Math.sqrt(SURF_R * SURF_R - EYE_X * EYE_X - 0.03 * 0.03) - 0.012;
const MOUTH_Y = EYE_Y - 0.12;
const MOUTH_Z = SURF_R - 0.012;

const GhostAvatar: React.FC<{
  transformRef: React.MutableRefObject<GhostTransform>;
  name?: string;
  /** Peer's avatar is still downloading: same ghost, fainter. */
  loading?: boolean;
}> = ({ transformRef, name, loading = false }) => {
  const s = getShared();
  const root = useRef<THREE.Group>(null);
  const body = useRef<THREE.Group>(null);
  const shadowRef = useRef<THREE.Mesh>(null);
  const phase = useMemo(() => hashPhase(name || 'ghost'), [name]);
  const placed = useRef(false);

  useFrame((state, delta) => {
    const g = root.current;
    if (!g) return;
    const t = transformRef.current;
    const k = Math.min(1, delta * 8);
    _target.set(t.position[0], t.position[1], t.position[2]);
    // Snap on first frame / teleports, otherwise glide like the avatars do.
    if (!placed.current || g.position.distanceToSquared(_target) > 25) {
      g.position.copy(_target);
      g.rotation.y = t.rotation;
      placed.current = true;
    } else {
      g.position.lerp(_target, k);
      const diff = t.rotation - g.rotation.y;
      g.rotation.y += Math.atan2(Math.sin(diff), Math.cos(diff)) * k;
    }
    const time = state.clock.elapsedTime;
    s.time.value = time;
    const b = body.current;
    if (b) {
      const bob = Math.sin(time * 1.6 + phase);
      b.position.y = bob * 0.05;
      b.rotation.z = Math.sin(time * 1.1 + phase) * 0.04;
      b.rotation.x = Math.sin(time * 0.9 + phase * 1.7) * 0.025;
      if (shadowRef.current) {
        const sc = 1 - bob * 0.08;
        shadowRef.current.scale.set(sc, 1, sc);
      }
    }
  });

  return (
    <group ref={root} position={transformRef.current.position} rotation={[0, transformRef.current.rotation, 0]}>
      {/* Fake soft contact shadow — stays on the floor while the ghost bobs. */}
      <mesh ref={shadowRef} geometry={s.shadow} material={s.shadowMat} position={[0, 0.015, 0]} renderOrder={-1} />
      <group ref={body}>
        <mesh geometry={s.body} material={loading ? s.bodyMatLoading : s.bodyMat} />
        {/* Eyes: dark ovals set into the front of the dome. */}
        <mesh geometry={s.eye} material={s.faceMat} position={[-EYE_X, EYE_Y, EYE_Z]} scale={[0.042, 0.065, 0.03]} rotation={[0, -0.32, 0]} />
        <mesh geometry={s.eye} material={s.faceMat} position={[EYE_X, EYE_Y, EYE_Z]} scale={[0.042, 0.065, 0.03]} rotation={[0, 0.32, 0]} />
        {/* Small "o" mouth */}
        <mesh geometry={s.mouth} material={s.faceMat} position={[0, MOUTH_Y, MOUTH_Z]} scale={[0.028, 0.022, 0.02]} />
        {/* Little arm nubs */}
        <mesh geometry={s.arm} material={loading ? s.bodyMatLoading : s.bodyMat} position={[-0.33, HOVER + 0.75, 0.04]} rotation={[0, 0, 0.9]} scale={[0.07, 0.17, 0.07]} />
        <mesh geometry={s.arm} material={loading ? s.bodyMatLoading : s.bodyMat} position={[0.33, HOVER + 0.75, 0.04]} rotation={[0, 0, -0.9]} scale={[0.07, 0.17, 0.07]} />
      </group>
      {/* The tag ignores its position prop (fixed at 1.92 m); lift it a touch
          so it clears the bobbing dome. */}
      {name && <group position={[0, 0.06, 0]}><FloatingNameTagWithEvents name={name} position={[0, 0, 0]} /></group>}
    </group>
  );
};

export default GhostAvatar;
